/**
 * What the Help form attaches about the device, and the console errors behind it.
 *
 * `installDiagnostics` runs once at app start (main.tsx) so the buffer already
 * holds what went wrong before anyone opened the form. It keeps the last few
 * error lines, scrubbed of email addresses and phone numbers, in memory only. Nothing here
 * reads a roster, a player, or the page text.
 */

import { environment, network } from '@good-season/platform';
import {
  SUPPORT_CONSOLE_ERROR_CHARS,
  SUPPORT_CONSOLE_ERRORS_MAX,
  type SupportDiagnostics,
} from '@good-season/schema';

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PHONE = /(?:\+?\d{1,3}[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/g;

const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

const lines: string[] = [];
let installed = false;

export function scrubLine(text: string): string {
  return text
    .replace(EMAIL, '[removed]')
    .replace(PHONE, '[removed]')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, SUPPORT_CONSOLE_ERROR_CHARS)
    .replace(LONE_SURROGATE, '\uFFFD');
}

function describe(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value;
  // An object logged next to an error may hold API data (a roster, a health record). Only
  // what names the error is kept: its kind, never its contents.
  if (typeof value === 'object' && value !== null) {
    const { name, message } = value as { name?: unknown; message?: unknown };
    if (typeof name === 'string' && typeof message === 'string') return `${name}: ${message}`;
    return Array.isArray(value) ? '[array]' : '[object]';
  }
  return String(value);
}

function remember(text: string): void {
  const line = scrubLine(text);
  if (line.length === 0) return;
  lines.push(line);
  if (lines.length > SUPPORT_CONSOLE_ERRORS_MAX) lines.shift();
}

export function installDiagnostics(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const original = console.error.bind(console);
  console.error = (...args: unknown[]): void => {
    try {
      remember(args.map(describe).join(' '));
    } catch {
      // Capturing must never break the call it observes.
    }
    original(...args);
  };
  window.addEventListener('error', (event) => {
    remember(`${event.message} (${event.filename.split('/').pop() ?? ''}:${String(event.lineno)})`);
  });
  window.addEventListener('unhandledrejection', (event) => {
    remember(`Unhandled rejection: ${describe(event.reason)}`);
  });
}

export function recentErrors(): readonly string[] {
  return [...lines];
}

/** Tests start from an empty buffer. */
export function clearDiagnosticsForTests(): void {
  lines.length = 0;
  installed = false;
}

export function collectDiagnostics(): SupportDiagnostics {
  const language = environment.language();
  return {
    browser: environment.browserLabel(),
    online: network.isOnline(),
    ...(language === null ? {} : { language }),
    consoleErrors: [...lines],
  };
}
